/**
 * Orquestração pura do picker/fila de revisão de Respostas Prontas (S5 T1 —
 * extração invisível p/ reuso RN). Vivia dentro de `ReviewQueue.tsx`/
 * `CannedResponsePicker.tsx`/`items-summary.ts` (web); movida aqui p/ o
 * composer mobile (T3/T4) reusar SEM duplicar o motor (regra de ouro:
 * cliente fino). Comportamento e copy PT são byte-a-byte idênticos ao
 * código web original — os 55 testes web são a rede de segurança (nenhuma
 * mudança de comportamento nesta extração).
 *
 * Variância do mapa de mídia: `ReadonlyMap<string, CannedMediaInfo>` (não
 * `Map`) — o `Map<string, MediaAssetWire>` do web tem MAIS campos que
 * `CannedMediaInfo` (kind/mimeType/sizeBytes), mas `Map.set` é invariante
 * no valor, então `Map<string, MediaAssetWire>` não seria atribuível a
 * `Map<string, CannedMediaInfo>`. `ReadonlyMap` (sem `set`) é covariante no
 * valor — aceita o `Map` mais específico do web/RN nos call sites.
 */
import { CHANNEL_MEDIA_RULES, evaluateChannelMedia } from '../channels/media-rules';
import { CANNED_VARIABLE_CATALOG, applyCannedVariables, extractCannedVariables } from './variables';
/** Nomes de variável de camada 2 (contrato/ERP) — derivado do catálogo único. */
export const LAYER2_VARIABLE_NAMES = new Set(CANNED_VARIABLE_CATALOG.filter((v) => v.layer === 2).map((v) => v.name));
// ── Fila de revisão (ex-`ReviewQueue.tsx`) ──────────────────────────────
/** = `buildInitialItems` do web. Monta a fila a partir dos items da resposta + variáveis já aplicadas. */
export function buildCannedQueueItems(items, variables) {
    return items.map((item) => {
        if (item.kind === 'text') {
            const { text, unresolved } = applyCannedVariables(item.text, variables);
            return { itemId: item.id, kind: 'text', text, unresolved, status: 'pending' };
        }
        if (item.caption !== undefined) {
            const { text: caption, unresolved } = applyCannedVariables(item.caption, variables);
            return {
                itemId: item.id,
                kind: 'media',
                text: '',
                unresolved,
                mediaAssetId: item.mediaAssetId,
                caption,
                status: 'pending',
            };
        }
        return {
            itemId: item.id,
            kind: 'media',
            text: '',
            unresolved: [],
            mediaAssetId: item.mediaAssetId,
            status: 'pending',
        };
    });
}
/** = `withEditedValue` do web. Recalcula `unresolved` a partir do novo valor editado. */
export function editCannedQueueItem(item, value) {
    const unresolved = extractCannedVariables(value);
    return item.kind === 'text'
        ? { ...item, text: value, unresolved }
        : { ...item, caption: value, unresolved };
}
/** = `toWireItem` do web. Projeta o item da fila para o payload do burst. */
export function cannedQueueItemToBurstRequest(item) {
    if (item.kind === 'text') {
        return { itemId: item.itemId, kind: 'text', text: item.text };
    }
    const mediaAssetId = item.mediaAssetId ?? '';
    return item.caption !== undefined
        ? { itemId: item.itemId, kind: 'media', mediaAssetId, caption: item.caption }
        : { itemId: item.itemId, kind: 'media', mediaAssetId };
}
/** = `applyResultStatus` do web. Aplica o resultado do burst preservando o shape do item. */
export function applyCannedBurstResult(item, result) {
    const base = {
        itemId: item.itemId,
        kind: item.kind,
        text: item.text,
        unresolved: item.unresolved,
        status: result.status,
        ...(item.mediaAssetId !== undefined ? { mediaAssetId: item.mediaAssetId } : {}),
        ...(item.caption !== undefined ? { caption: item.caption } : {}),
    };
    return result.failureReason !== undefined
        ? { ...base, failureReason: result.failureReason }
        : base;
}
/**
 * = `mediaBlock` do web. Verdito de mídia por-item via o catálogo por canal
 * (S3, `CHANNEL_MEDIA_RULES`/`evaluateChannelMedia`). 2 níveis: Tier 1 —
 * canal SEM adapter de mídia nenhum bloqueia toda mídia sem precisar do
 * asset carregado; Tier 2 — canal com adapter precisa do asset real
 * (kind/mimeType/sizeBytes) p/ veredito preciso; asset ainda não carregado
 * ⇒ não bloqueia aqui (o Engine é autoritativo no burst).
 */
export function evaluateCannedMediaBlock(item, channel, mediaById) {
    if (item.kind !== 'media' || item.mediaAssetId === undefined)
        return null;
    const channelRules = CHANNEL_MEDIA_RULES[channel];
    if (channelRules === undefined)
        return { reason: 'kind-unsupported' };
    const asset = mediaById.get(item.mediaAssetId);
    if (asset === undefined)
        return null;
    const verdict = evaluateChannelMedia({
        channel,
        kind: asset.kind,
        mimeType: asset.mimeType,
        sizeBytes: asset.sizeBytes,
    });
    return verdict.ok ? null : { reason: verdict.reason };
}
/** = `mediaKindNoun` do web. */
export function cannedMediaKindNoun(kind) {
    switch (kind) {
        case 'image':
            return 'imagem';
        case 'video':
            return 'vídeo';
        case 'audio':
            return 'áudio';
        default:
            return 'mídia';
    }
}
/**
 * = `itemNote` do web. Nota âmbar por-item — canal incompatível tem
 * prioridade sobre variável pendente. Dentro de camada 2, distingue
 * verified×não-verificado (Important #2 pós-review S2 T9): não-verificado
 * → "exige titular verificado"; verificado (a var só pode ter falhado por
 * ERP/MCP) → "não foi possível obter o dado do contrato" — a copy antiga
 * mentiria nesse ramo.
 */
export function cannedItemNote(item, channel, mediaById, verified) {
    const block = evaluateCannedMediaBlock(item, channel, mediaById);
    if (block !== null) {
        const asset = item.mediaAssetId !== undefined ? mediaById.get(item.mediaAssetId) : undefined;
        const noun = cannedMediaKindNoun(asset?.kind);
        if (block.reason === 'mime-unsupported') {
            return `Formato de ${noun} não aceito neste canal — remova o item ou use outro arquivo.`;
        }
        if (block.reason === 'too-large') {
            const capNoun = noun.charAt(0).toUpperCase() + noun.slice(1);
            return `${capNoun} grande demais para este canal — remova o item.`;
        }
        return `Este canal não recebe ${noun} — remova o item para enviar a sequência.`;
    }
    if (item.unresolved.length > 0) {
        const hasLayer2 = item.unresolved.some((name) => LAYER2_VARIABLE_NAMES.has(name));
        if (hasLayer2) {
            return verified
                ? 'Não foi possível obter o dado do contrato — edite o texto ou remova o item.'
                : 'Dado de contrato exige titular verificado — edite o texto ou remova o item.';
        }
        return 'Preencha a variável antes de enviar.';
    }
    return null;
}
/** = `aggregateBlockReason` do web. Copy agregada do banner de bloqueio. */
export function aggregateCannedBlockReason(blockedItems, channel, mediaById) {
    const varBlocked = blockedItems.filter((it) => it.unresolved.length > 0).length;
    const mediaBlocked = blockedItems.filter((it) => evaluateCannedMediaBlock(it, channel, mediaById) !== null && it.unresolved.length === 0).length;
    const parts = [];
    if (varBlocked > 0) {
        parts.push(`${varBlocked.toString()} ${varBlocked === 1 ? 'item com variável pendente' : 'itens com variáveis pendentes'}`);
    }
    if (mediaBlocked > 0) {
        parts.push(`${mediaBlocked.toString()} ${mediaBlocked === 1 ? 'item de mídia incompatível com o canal' : 'itens de mídia incompatíveis com o canal'}`);
    }
    return `Resolva antes de enviar: ${parts.join(' · ')}.`;
}
// ── Resumo de items (ex-`items-summary.ts`) ─────────────────────────────
function mediaKindLabel(kind, count) {
    switch (kind) {
        case 'image':
            return count === 1 ? 'imagem' : 'imagens';
        case 'video':
            return count === 1 ? 'vídeo' : 'vídeos';
        case 'audio':
            return count === 1 ? 'áudio' : 'áudios';
        default:
            return count === 1 ? 'mídia' : 'mídias';
    }
}
/**
 * Resumo honesto dos items de uma Resposta Pronta ("3 mensagens · 1
 * imagem") — usado na tabela da biblioteca e no picker.
 */
export function summarizeCannedResponseItems(items, mediaById) {
    if (items.length === 0)
        return 'Sem itens';
    const textCount = items.filter((i) => i.kind === 'text').length;
    const kindCounts = new Map();
    for (const item of items) {
        if (item.kind !== 'media')
            continue;
        const kind = mediaById.get(item.mediaAssetId)?.kind ?? 'mídia';
        kindCounts.set(kind, (kindCounts.get(kind) ?? 0) + 1);
    }
    const parts = [];
    if (textCount > 0)
        parts.push(`${textCount.toString()} ${textCount === 1 ? 'mensagem' : 'mensagens'}`);
    for (const [kind, count] of kindCounts) {
        parts.push(`${count.toString()} ${mediaKindLabel(kind, count)}`);
    }
    return parts.join(' · ');
}
// ── Picker (ex-`CannedResponsePicker.tsx`) ──────────────────────────────
const DEFAULT_EXCERPT_MAX_CHARS = 64;
/** = `matchesQuery` do web. Busca client-side por título/categoria/shortcut/conteúdo de texto. */
export function matchesCannedQuery(response, query) {
    if (query === '')
        return true;
    const needle = query.toLowerCase();
    if (response.title.toLowerCase().includes(needle))
        return true;
    if ((response.category ?? '').toLowerCase().includes(needle))
        return true;
    if ((response.shortcut ?? '').toLowerCase().includes(needle))
        return true;
    return response.items.some((item) => item.kind === 'text' && item.text.toLowerCase().includes(needle));
}
/** = partição inline do picker. Vinculadas ao playbook ativo primeiro, demais na biblioteca. */
export function partitionCannedByPlaybook(responses, playbookId) {
    const isLinked = (r) => playbookId !== null && r.playbookIds.includes(playbookId);
    return {
        linked: responses.filter(isLinked),
        library: responses.filter((r) => !isLinked(r)),
    };
}
/** = `firstTextExcerpt` do web. Trecho do 1º item de texto (default 64 chars, sufixo '…'). */
export function cannedFirstTextExcerpt(response, maxChars = DEFAULT_EXCERPT_MAX_CHARS) {
    const firstText = response.items.find((item) => item.kind === 'text');
    if (firstText === undefined)
        return null;
    const trimmed = firstText.text.trim();
    if (trimmed === '')
        return null;
    return trimmed.length > maxChars ? `${trimmed.slice(0, maxChars)}…` : trimmed;
}
//# sourceMappingURL=review.js.map